;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
} from 'react'
import { browserLogger } from 'src/core/logger/browser'
import { useRefreshSession } from 'src/deprecated/hooks/api/usePostAuthRefresh'
import { useLandingRoute } from 'src/deprecated/hooks/utils/useLandingRoute'
import { getAccessToken } from 'src/deprecated/utils/authentication'
import { getPrivateQueryKey } from 'src/deprecated/utils/queryKey'
import {
  removeUserIdentificationFromThirdParties,
  trackUserSignedOut,
} from 'src/deprecated/utils/report/tracking'

type AuthStatus = 'VERIFIED' | 'INVALID' | 'NOT_SET'

type OptionalContext<T> = T | undefined

const AuthStatusContext = createContext<OptionalContext<AuthStatus>>(undefined)
const LogoutContext = createContext<OptionalContext<() => void>>(undefined)
const SignatureContext =
  createContext<OptionalContext<string | null>>(undefined)

const fetchLogout = async (accessToken: string) => {
  return fetch(`/api/auth/logout/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
}

type AuthenticationProviderProps = {
  children: ReactElement
}

export function AuthenticationProvider({
  children,
}: AuthenticationProviderProps) {
  const queryClient = useQueryClient()

  const {
    invalidate: invalidateRefreshSession,
    data: refreshToken,
    isError,
  } = useRefreshSession()

  const logout = useCallback(async () => {
    const lastToken = await getAccessToken()
    try {
      await fetchLogout(lastToken)
    } catch (error) {
      browserLogger.error(error)
    } finally {
      removeUserIdentificationFromThirdParties()
      trackUserSignedOut()
      queryClient.clear()
      invalidateRefreshSession()
      window.location.reload()
    }
  }, [queryClient, invalidateRefreshSession])

  let authStatus: AuthStatus = 'NOT_SET'

  if (refreshToken) authStatus = 'VERIFIED'
  if (isError) authStatus = 'INVALID'

  useEffect(() => {
    if (authStatus === 'INVALID') {
      queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] === getPrivateQueryKey(),
      })
    }
  }, [authStatus, queryClient])

  return (
    <SignatureContext.Provider value={refreshToken?.signature ?? null}>
      <AuthStatusContext.Provider value={authStatus || 'INVALID'}>
        <LogoutContext.Provider value={logout}>
          {children}
        </LogoutContext.Provider>
      </AuthStatusContext.Provider>
    </SignatureContext.Provider>
  )
}

export const useSignature = () => {
  const value = useContext(SignatureContext)

  if (value === undefined) throw new Error('SignatureContext is not available')

  return value
}

export const useAuthStatus = (): AuthStatus => {
  const value = useContext(AuthStatusContext)

  if (value === undefined) {
    throw new Error('AuthStatusContext is not available')
  }

  return value
}

export const useLogout = () => {
  const value = useContext(LogoutContext)

  if (value === undefined) {
    throw new Error('LogoutContext is not available')
  }

  return value
}

export function useAppRedirection(shouldRedirectToLogin: boolean) {
  const landingRoute = useLandingRoute()
  const { replace, push, query, asPath } = useRouter()
  const redirectParameter =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- grandfathered: deprecated-misc
    (query.redirectPath as string) ??
    // oxlint-disable-next-line typescript/consistent-type-assertions -- grandfathered: deprecated-misc
    (query.redirectTo as string) ??
    landingRoute

  const onAppMount = useCallback(() => {
    if (shouldRedirectToLogin) {
      const redirectValue = new URLSearchParams({ redirectPath: asPath })

      replace(`/login?${redirectValue}`)
    }
  }, [asPath, replace, shouldRedirectToLogin])

  const redirectVisitor = useCallback(() => {
    push(redirectParameter)
  }, [push, redirectParameter])

  return {
    onAppMount,
    redirectVisitor,
  }
}
